//@import dependencies
import React, {useEffect, useState} from 'react'
import { useTranslation } from 'react-i18next'
import { BiError } from 'react-icons/bi'
import { useVirtual, VirtualItem } from 'react-virtual'
//@end

// import component 👇
import IconsComponent from 'app_components/commons/icons'
import Drawer from 'react-modern-drawer'

//import styles 👇
import 'react-modern-drawer/dist/index.css'

//@import components
import BreadcrumbComponent from 'app_components/commons/breadcrumb'
import DataTableComponent from 'app_components/commons/dataTable'
import JSONView from 'app_components/commons/viewer/JSONView'
import AppContainer from 'app_components/layouts/app'
import DocumentProcessorSkeleton from './documentProcessorSkeleton'
//@end

// @import hooks
import { useColors } from 'app_hooks/useColors'
// @end

// @INFO Import types
import { IPagination } from 'app_types/pagination/paginationTypes'
// @end

// @INFO Import services
// @end

// @INFO Import Styles
import DocumentProcessorService from 'app_services/admin/documentProcessor/documentProcessorService'
import { DocumentProcessorOptions, DocumentProcessorStatus, IDocumentLogs, IDocumentProcessorItem } from 'app_types/admin/documentProcessor/documentProcessorTypes'
import { ButtonGroup, Spinner, ToggleButton } from 'react-bootstrap'
import { FaFileDownload } from 'react-icons/fa'
import { VscOpenPreview } from 'react-icons/vsc'
import './../documentProcessorScreen.scss'

// @ADD Component types
interface IDocumentProcessorScreenLayoutProps {
  itemList: IDocumentProcessorItem[]
  status: null | 'loading' | 'error'
  pagination: IPagination
  onPageChange: (page: number) => void
  remove: (id: string) => void
}

const DocumentProcessorScreenLayout: React.FC<IDocumentProcessorScreenLayoutProps> = (props) => {

  const [loadingLogs, updateLoadingLogs] = useState<boolean>(false)
  const [logsData, updateLogsData] = useState<IDocumentLogs & {filters: IDocumentLogs} | undefined>(undefined)
  const [renderingLogs, updateRenderingLogs] = useState<boolean>(false)

  const [searchInput, updateSearchInput] = useState<string | null>(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [radioValue, setRadioValue] = useState(DocumentProcessorOptions.CARD)

  const toggleDrawer = () => {
    setIsOpen((prevState) => !prevState)
  }

  const { t } = useTranslation()
  const [colors] = useColors()

  const radios = [
    { name: t('screens.documentProcessor.drawer.options.card'), value: DocumentProcessorOptions.CARD },
    { name: t('screens.documentProcessor.drawer.options.json'), value: DocumentProcessorOptions.JSON },
  ]

  const documentProcessorService = new DocumentProcessorService()

  const checkStatusAndLog = (item: IDocumentProcessorItem) => {
    if (item.status) {
      let title = t('screens.documentProcessor.documentStatus.processing')
      let icon = <Spinner className='spinner-loading' animation='border' size='sm' />
      let component: any = (
        <button
          className='btn btn-link btn-sm'
          data-tip={title}
        >
          {icon}
        </button>
      )
      if (item.status === DocumentProcessorStatus.COMPLETE) {
        title = t('screens.documentProcessor.documentStatus.success')
        icon = <VscOpenPreview size='20px' color={colors.primary} />
        component = (
          <button
            className='btn btn-link btn-sm'
            data-tip={title}
            onClick={() => fetchLogs(item)}
            disabled={loadingLogs}
          >
            {icon}
          </button>
        )
      } else if (item.status === DocumentProcessorStatus.ERROR) {
        title = t('screens.documentProcessor.documentStatus.error')
        icon = <BiError size='20px' color={colors.tertiary} />
        component = (
          <button
            className='btn btn-link btn-sm'
            data-tip={title}
          >
            {icon}
          </button>
        )
      }

      return component
    }
  }

  const fetchLogs = async (item: IDocumentProcessorItem) => {
    updateLogsData({
      errorLog: undefined,
      processLog: undefined,
      filters: {
        errorLog: undefined,
        processLog: undefined,
      },
    })
    updateSearchInput(null)
    setRadioValue(DocumentProcessorOptions.CARD)

    updateLoadingLogs(true)
    const response = await documentProcessorService.list({
      id: item._id,
      select: 'id errorLog processLog',
    })
    if (response?.code === 200 && response?.documentQueue && response?.documentQueue[0]) {
      updateLogsData({
        errorLog: response.documentQueue[0].errorLog || undefined,
        processLog: response.documentQueue[0].processLog || undefined,
        filters: {
          errorLog: response.documentQueue[0].errorLog || undefined,
          processLog: response.documentQueue[0].processLog || undefined,
        },
      })
      toggleDrawer()
    }
    updateLoadingLogs(false)
  }

  const changeRadio = (value: DocumentProcessorOptions) => {
    updateRenderingLogs(true)
    setRadioValue(value)
  }

  const searchLogs = (event: any) => {
    const search = event.target.value
    updateSearchInput(search)
    if (search.length >= 1) {
      const condition = new RegExp(`${search}`,'i')
      let errorFilter = logsData?.filters?.errorLog || []
      let processFilter = logsData?.filters?.processLog || []
      if (logsData?.errorLog && logsData?.filters?.errorLog) {
        errorFilter = logsData?.errorLog.filter(function (obj) {
          return Object.keys(obj).some(function(key) {
            return condition.test(obj[key])
          })
        })
      }
      if (logsData?.processLog && logsData?.filters?.processLog) {
        processFilter = logsData?.processLog.filter(function (obj) {
          return Object.keys(obj).some(function(key) {
            return condition.test(obj[key])
          })
        })
      }
      updateLogsData({
        ...logsData,
        filters: {
          errorLog: errorFilter,
          processLog: processFilter,
        },
      })
    } else if (search.length === 0) {
      updateLogsData({
        ...logsData,
        filters: {
          errorLog: logsData?.errorLog,
          processLog: logsData?.processLog,
        },
      })
    }
  }
  useEffect(() => {
    if (radioValue) {
      updateRenderingLogs(false)
    }
  }, [radioValue])

  return(
    <>
      <AppContainer>
        <div className='document_processor-component container-fluid'>
          <BreadcrumbComponent links={['#']} texts={[`${t('components.breadcrumb.documentProcessor')}`]} />

          <div className='document-processor-screen'>

            {props.status === 'loading' && (
              <DocumentProcessorSkeleton />
            )}

            {props.status !== 'loading' && (
              <>
                <h2>{t('screens.documentProcessor.title')}</h2>

                <DataTableComponent
                  data={props.itemList}
                  headerKeys={[
                    `${t('components.dataTable.headers.documentProcessor.type')}`,
                    `${t('components.dataTable.headers.documentProcessor.status')}`,
                    `${t('components.dataTable.headers.documentProcessor.date')}`,
                  ]}
                  keys={['typeLabel','statusLabel', 'created_at']}
                  pagination={props.pagination}
                  detailsKey={[]}
                  onPageChange={props.onPageChange}
                  onModal={null}
                  rowButtons={(item: IDocumentProcessorItem) => {
                    return (
                      <>
                        {item.fileUrl && (
                          <button
                            className='btn btn-link btn-sm'
                            data-tip={t('screens.documentProcessor.download_file')}
                            onClick={() => window.open(item?.fileUrl, '_blank')}
                            >
                            <FaFileDownload size='20px' color={colors.primary} />
                          </button>
                        )}
                        {checkStatusAndLog(item)}
                        {props?.remove && item.status && [DocumentProcessorStatus.IN, DocumentProcessorStatus.NEW].includes(item.status) && (
                          <button
                            className='btn btn-link btn-sm'
                            onClick={() => props.remove(item?._id)}
                            data-tip={t('components.tooltips.delete')}
                          >
                            <IconsComponent svg='Delete' width='24px' height='24px' />
                          </button>
                        )}
                      </>
                    )
                  }}
                />
              </>
            )}
          </div>
        </div>
        <Drawer
          open={isOpen}
          onClose={toggleDrawer}
          direction='right'
          className=''
          size={'50vw'}
          style={{padding: '5px'}}
        >
          <div className='form-group row'>
            <label htmlFor='search' className='col-sm-2 col-form-label'>Buscador:</label>
            <div className='col-sm-6'>
              <input type='text' placeholder='Buscador...' className='form-control' id='search' value={searchInput || ''} onChange={(event) => searchLogs(event)} />
            </div>
            <div className='col-sm-4'>
              <ButtonGroup toggle>
                {radios.map((radio, idx) => (
                  <ToggleButton
                    key={idx}
                    type='radio'
                    variant='secondary'
                    name='radio'
                    value={radio.value}
                    checked={radioValue === radio.value}
                    onChange={(e) => changeRadio(radio.value)}
                  >
                    {radio.name}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </div>
          </div>
          <div className='form-group row'>
            <label htmlFor='view' className='col-sm-6 col-form-label'>Modo de visualización:</label>
          </div>
          <hr />
          {renderingLogs ? (
            <button
              className='btn btn-link btn-sm'
              disabled
            >
              <Spinner className='spinner-loading' animation='border' size='sm' />
            </button>
          ) : (
            <>
              {logsData?.processLog && logsData?.filters?.processLog && (
                <div className='form-group'>
                  <label>Items procesados de forma correcta</label>
                  {logsData?.filters?.processLog.length > 0 ? (
                    <DataVirtualizer rows={logsData?.filters.processLog} component={radioValue} />
                  ): (
                    <div className='alert alert-info'>No hay datos para mostrar</div>
                  )}
                </div>
              )}

              {logsData?.errorLog && logsData?.filters?.errorLog && (
                <div className='form-group'>
                  <div>Items procesados con errores</div>
                  {logsData?.filters?.errorLog.length > 0 ? (
                    <DataVirtualizer rows={logsData?.filters.errorLog} component={radioValue} />
                  ): (
                    <div className='alert alert-info'>No hay datos para mostrar</div>
                  )}
                </div>
              )}
            </>
          )}
        </Drawer>
      </AppContainer>
    </>
  )
}

const CardLog = ({content}: {content: any}) => {
  return (
    <div className='card-log-container'>
      {content?.index && content?.sheetName &&(
        <div className='card-log-row'>
          <div><b>Hoja:</b></div>
          <div className='card-log-item'>{content.sheetName}</div>
          <div><b>Fila:</b></div>
          <div className='card-log-item'>{content.index}</div>
        </div>
      )}
      {content?.row && content?.sheetName && (
        <div className='card-log-row'>
          <div><b>Hoja:</b></div>
          <div className='card-log-item'>{content.sheetName}</div>
          <div><b>Fila:</b></div>
          <div className='card-log-item'>{content.row}</div>
        </div>
      )}
      {content?.errorType && (
        <div className='card-log-row'>
          <div><b>Error:</b></div>
          <div>
            {content?.message && (
              <div className='card-log-item'>{content.message}</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


const DataVirtualizer = ({rows, component}: {rows: any[], component: DocumentProcessorOptions}) => {
  const parentRef = React.useRef()

  const rowVirtualizer = useVirtual({
    size: rows.length,
    parentRef,
  })

  const renderComponent = (virtualRow: VirtualItem) => {
    if (component === 'json') {
      return <JSONView key={virtualRow.index} json={rows[virtualRow.index]} />
    } else if (component === 'card') {
      return (<CardLog content={rows[virtualRow.index]} />)
    }
    return null
  }

  return (
    <>
      <div
        ref={parentRef}
        className=''
        style={{
          height: `40vh`,
          width: `100%`,
          overflow: 'auto',
        }}
      >
        <div
          style={{
            height: `${rowVirtualizer.totalSize}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.virtualItems.map(virtualRow => (
            <div
              key={virtualRow.index}
              ref={virtualRow.measureRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div style={{ height: rows[virtualRow.index] }}>
                {renderComponent(virtualRow)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default DocumentProcessorScreenLayout
